import {memo} from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Badge} from '@common-web/components/ui/badge';
import {PrunedMappingInfo, RemovedMappingInfo} from '@client/pages/payroll/utils/columnValidation';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';

type RemovedMappingAlert = {
  show: boolean;
  removedMappings: RemovedMappingInfo[];
  prunedMappings: PrunedMappingInfo[];
};

export const AutoRemovedMappingAlert = memo(AutoRemovedMappingAlertComponent);

function AutoRemovedMappingAlertComponent({
  removedMappingsAlert,
  setRemovedMappingsAlert,
  getTargetFieldLabel,
}: {
  removedMappingsAlert: RemovedMappingAlert;
  setRemovedMappingsAlert: React.Dispatch<React.SetStateAction<RemovedMappingAlert>>;
  getTargetFieldLabel: (field: PayrollTargetField) => string;
}) {
  const hasPruned = removedMappingsAlert.prunedMappings.length > 0;
  const hasRemoved = removedMappingsAlert.removedMappings.length > 0;

  return (
    <AlertDialog
      open={removedMappingsAlert.show}
      onOpenChange={(open) => {
        if (!open) {
          setRemovedMappingsAlert((prev) => ({...prev, show: false}));
        }
      }}
    >
      <AlertDialogContent className='max-w-lg p-0 overflow-hidden'>
        <AlertDialogHeader className='px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100'>
          <div className='flex items-center gap-3'>
            <div>
              <AlertDialogTitle className='text-lg font-semibold text-gray-900'>
                Column Mapping Changes
              </AlertDialogTitle>
              <p className='text-sm text-gray-600 mt-0.5'>
                Some mappings were updated based on your uploaded files
              </p>
            </div>
          </div>
        </AlertDialogHeader>
        <div className='px-6 pb-4 overflow-y-auto max-h-[400px] space-y-5'>
          {hasPruned && (
            <div>
              <div className='flex items-center gap-2 mb-3'>
                <span className='text-base font-medium text-gray-700'>Partially Updated</span>
              </div>
              <p className='text-sm text-gray-500 mb-3'>
                Some columns were removed from these mappings, but the remaining columns are still
                configured.
              </p>
              <div className='space-y-2'>
                {removedMappingsAlert.prunedMappings.map((prunedMapping, idx) => (
                  <div
                    key={`pruned-${idx}`}
                    className='p-3 rounded-lg border bg-white border-gray-200'
                  >
                    <div className='flex items-center justify-between gap-2 mb-2'>
                      <span className='font-medium text-gray-900'>
                        {getTargetFieldLabel(prunedMapping.targetField)}
                      </span>
                      <Badge
                        variant='outline'
                        className='text-xs text-center border-emerald-300 bg-emerald/10 text-emerald'
                      >
                        {prunedMapping.remainingColumnCount} column
                        {prunedMapping.remainingColumnCount !== 1 ? 's' : ''} remaining
                      </Badge>
                    </div>
                    <div className='text-xs text-gray-600 mb-1.5'>Removed columns:</div>
                    <div className='flex flex-wrap gap-1.5'>
                      {prunedMapping.prunedColumns.map((col, i) => (
                        <div
                          key={i}
                          className='font-mono text-xs text-gray-700 bg-white border border-gray-200 rounded-md p-2'
                        >
                          {col.fileId}: <span className={'font-medium'}>{col.columnName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasPruned && hasRemoved && <div className='border-t border-gray-100' />}
          {hasRemoved && (
            <div>
              <div className='flex items-center gap-2 mb-3'>
                <span className='text-base font-medium text-gray-700'>Removed Mappings</span>
              </div>
              <p className='text-sm text-gray-500 mb-3'>
                These mappings were removed because their referenced columns are no longer
                available.
              </p>
              <div className='space-y-2'>
                {removedMappingsAlert.removedMappings.map((removedMapping, idx) => (
                  <div
                    key={`removed-${idx}`}
                    className='p-3 rounded-lg border bg-white border-gray-200'
                  >
                    <div className='flex items-center justify-between gap-2 mb-2'>
                      <span className='font-medium text-gray-900'>
                        {getTargetFieldLabel(removedMapping.targetField)}
                      </span>
                      <Badge
                        variant='outline'
                        className='text-xs border-red-300 bg-red-50 text-red-600'
                      >
                        Removed
                      </Badge>
                    </div>
                    {removedMapping.missingColumns.length > 0 && (
                      <>
                        <div className='text-xs text-gray-600 mb-1.5'>Missing columns:</div>
                        <div className='flex flex-wrap gap-1.5'>
                          {removedMapping.missingColumns.map((missingColumn, i) => (
                            <div
                              key={i}
                              className='font-mono text-xs text-gray-700 bg-white border border-gray-200 rounded-md p-2'
                            >
                              {missingColumn.fileId}:{' '}
                              <span className={'font-medium'}>{missingColumn.columnName}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <AlertDialogFooter className='px-6 py-4 bg-gray-50 border-t border-gray-100'>
          <AlertDialogAction className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white px-6'>
            Okay
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
